import React, { useState, useEffect } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  Sparkles,
  ArrowLeft,
  RefreshCw,
  HelpCircle,
  CheckSquare,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import Loader from "../../../../app/shared/components/Loader";
import Alert from "../../../../app/shared/components/Alert";
import "../styles/QuestionBankStyles.css";

interface QuestionDetails {
  question_id: string;
  question: string;
  difficulty_level: string;
  explanation?: string;
  answers: { answer_id: string; answer: string; correct_answer: boolean }[];
}

const RephraseAIQuestionPage: React.FC = () => {
  const location = useLocation();
  const params = useParams<{ questionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const questionId = location.state?.questionId || params.questionId || "";
  const requestId =
    location.state?.requestId || searchParams.get("request_id") || "";
  const moduleId =
    location.state?.moduleId || searchParams.get("module_id") || "";

  const [questionData, setQuestionData] = useState<QuestionDetails | null>(
    null,
  );
  const [contextText, setContextText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [rephrasing, setRephrasing] = useState<boolean>(false);
  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "warning" | "error";
  }>({
    show: false,
    title: "",
    message: "",
    type: "success",
  });

  useEffect(() => {
    if (requestId && moduleId && questionId) {
      fetchQuestionDetails();
    }
  }, [questionId, requestId, moduleId]);

  const fetchQuestionDetails = async () => {
    try {
      setLoading(true);
      const res = await QuestionBankClient.getAIGenerationRequestDetails(
        requestId,
        moduleId,
      );
      if (res && res.questions) {
        const targetQ = res.questions.find(
          (q: any) => q.question_id === questionId,
        );
        if (targetQ) {
          setQuestionData(targetQ);
        }
      }
    } catch (err) {
      console.error("Error fetching question details for rephrase:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRephraseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contextText.trim()) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Please provide rephrase instructions or custom context.",
        type: "warning",
      });
      return;
    }

    setRephrasing(true);
    try {
      await QuestionBankClient.rephraseAIGeneratedQuestion(
        questionId!,
        requestId,
        moduleId,
        contextText.trim(),
      );
      setRephrasing(false);
      setAlertConfig({
        show: true,
        title: "Rephrase Success",
        message: "Question and options rephrased successfully using AI!",
        type: "success",
      });

      setTimeout(() => {
        navigate("/dashboard/question-bank/ai-review", {
          state: { requestId, moduleId },
        });
      }, 1000);
    } catch (err: any) {
      setRephrasing(false);
      setAlertConfig({
        show: true,
        title: "Rephrase Failed",
        message:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to rephrase question with AI.",
        type: "error",
      });
    }
  };

  return (
    <div className="qb-container">
      <Loader
        isLoading={loading || rephrasing}
        message={
          rephrasing
            ? "Rephrasing Question with AI..."
            : "Loading Question Details..."
        }
      />

      <Alert
        show={alertConfig.show}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, show: false }))}
      />

      {/* Corporate Header matching AddQuestionPage */}
      <div className="qb-header">
        <div>
          <h1 className="qb-title">
            <RefreshCw size={26} color="#0d6e5b" /> Rephrase Question
          </h1>
          <p className="qb-subtitle">
            Instruct AI to rewrite question text, choices, or explanation with
            custom context.
          </p>
        </div>

        {/* <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button
            type="button"
            className="qb-btn qb-btn-secondary"
            onClick={() =>
              navigate("/dashboard/question-bank/ai-review", {
                state: { requestId, moduleId },
              })
            }
          >
            <ArrowLeft size={16} /> Back to Review Workspace
          </button>
        </div> */}
      </div>

      {/* Form Container spanning full width matching AddQuestionPage */}
      {questionData ? (
        <form className="qb-single-frame-form" onSubmit={handleRephraseSubmit}>
          <div className="qb-form-body-scroll">
            {/* Section 1: Current Question Preview */}
            <div
              className="qb-form-section"
              style={{ padding: "14px 18px", marginBottom: "0" }}
            >
              <div
                className="qb-section-header"
                style={{ marginBottom: "10px", paddingBottom: "6px" }}
              >
                <CheckSquare size={18} color="#0d6e5b" />
                <span>1. Target Question Details</span>
              </div>

              <div
                style={{
                  backgroundColor: "#f8fafc",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid #dde5e2",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 10px",
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#1e293b",
                    lineHeight: "1.4",
                  }}
                >
                  {questionData.question}
                </h3>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  {questionData.answers &&
                    questionData.answers.map((opt, idx) => (
                      <div
                        key={opt.answer_id || idx}
                        style={{
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: opt.correct_answer
                            ? "1.5px solid #0d6e5b"
                            : "1px solid #dde5e2",
                          backgroundColor: opt.correct_answer
                            ? "#e6f4f1"
                            : "#ffffff",
                          fontSize: "13px",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: "8px",
                            color: opt.correct_answer ? "#0d6e5b" : "#64748b",
                          }}
                        >
                          {String.fromCharCode(65 + idx)}.
                        </span>
                        <span
                          style={{
                            fontWeight: opt.correct_answer ? 600 : 400,
                            color: "#1e293b",
                          }}
                        >
                          {opt.answer}
                        </span>
                      </div>
                    ))}
                </div>

                {questionData.explanation && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      borderTop: "1px solid #dde5e2",
                      marginTop: "8px",
                      paddingTop: "6px",
                    }}
                  >
                    <strong style={{ color: "#0d6e5b" }}>Explanation:</strong>{" "}
                    {questionData.explanation}
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Rephrase Instructions */}
            <div
              className="qb-form-section"
              style={{
                padding: "14px 18px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="qb-section-header"
                style={{ marginBottom: "10px", paddingBottom: "6px" }}
              >
                <Sparkles size={18} color="#0d6e5b" />
                <span>2. Rephrase Instructions & Custom Context</span>
              </div>

              <div
                className="qb-form-group"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: 0,
                }}
              >
                <label
                  className="qb-label"
                  style={{ display: "block", marginBottom: "6px" }}
                >
                  Rephrase Prompt Instructions{" "}
                  <span style={{ color: "#ef4444" }}>*</span>:
                </label>
                <textarea
                  className="qb-textarea"
                  rows={3}
                  style={{ flex: 1, minHeight: "80px", resize: "vertical" }}
                  value={contextText}
                  onChange={(e) => setContextText(e.target.value)}
                  placeholder="e.g. Make the question scenario-based for senior developers, focus on Garbage Collection memory tuning, or simplify the wording..."
                />
              </div>
            </div>
          </div>

          {/* Pinned Fixed Bottom Action Bar */}
          <div className="qb-fixed-action-bar">
            <button
              type="button"
              className="qb-btn qb-btn-outline"
              onClick={() =>
                navigate("/dashboard/question-bank/ai-review", {
                  state: { requestId, moduleId },
                })
              }
              style={{ padding: "8px 20px" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="qb-btn qb-btn-primary"
              style={{ padding: "8px 24px" }}
              disabled={rephrasing || !contextText.trim()}
            >
              <Sparkles size={16} />
              <span>Rephrase with AI</span>
            </button>
          </div>
        </form>
      ) : (
        <div
          className="qb-form-section"
          style={{ padding: "48px", textAlign: "center", color: "#64748b" }}
        >
          <HelpCircle
            size={48}
            style={{ opacity: 0.3, marginBottom: "12px" }}
          />
          <h3>Question Not Found</h3>
        </div>
      )}
    </div>
  );
};

export default RephraseAIQuestionPage;
